import { useCallback, useRef, useState } from 'react'
import axios from 'axios'
import { runConcurrent } from '../utils/concurrentDownload'

/**
 * Client-side cache of unit/transformation availability per indicator+country,
 * backed by POST /api/v1/data/indicator-country-metadata/ (IndicatorCountryMetadata).
 *
 * Mirrors the pattern already used by the dashboard widget: combinations are
 * fetched once per indicator+country and never recomputed client-side or
 * server-side — the backend already stores the real Unit/Transformation
 * combinations found in the data.
 *
 * Exposes `metadataByIndicator`, a merged (union across the currently
 * requested countries) view per indicator:
 *   { [indicatorName]: { [frequency]: { [unit]: string[] transformations } } }
 */
function useIndicatorMetadataCache() {
  // indicator -> Map(country -> metadata|null)
  const cacheRef = useRef(new Map())
  const [metadataByIndicator, setMetadataByIndicator] = useState({})
  const [loading, setLoading] = useState(false)

  const recomputeMerged = useCallback((indicators) => {
    setMetadataByIndicator((prev) => {
      const next = { ...prev }
      indicators.forEach((indicatorName) => {
        const countryMap = cacheRef.current.get(indicatorName)
        if (!countryMap) return

        const merged = {}
        countryMap.forEach((metadata) => {
          if (!metadata || !metadata.frequencies) return
          Object.entries(metadata.frequencies).forEach(([freq, freqData]) => {
            if (!merged[freq]) merged[freq] = {}
            const units = (freqData && freqData.units) || {}
            Object.entries(units).forEach(([unit, unitData]) => {
              if (!merged[freq][unit]) merged[freq][unit] = new Set()
              const transformations = (unitData && unitData.transformations) || []
              transformations.forEach((t) => merged[freq][unit].add(t))
            })
          })
        })

        const serializable = {}
        Object.entries(merged).forEach(([freq, units]) => {
          serializable[freq] = {}
          Object.entries(units).forEach(([unit, transformationSet]) => {
            serializable[freq][unit] = Array.from(transformationSet)
          })
        })
        next[indicatorName] = serializable
      })
      return next
    })
  }, [])

  const ensureMetadata = useCallback(async (indicatorNames, countryNames) => {
    const indicators = Array.from(new Set((indicatorNames || []).filter(Boolean)))
    const countries = Array.from(new Set((countryNames || []).filter(Boolean)))
    if (indicators.length === 0 || countries.length === 0) return

    const missing = []
    indicators.forEach((indicator) => {
      if (!cacheRef.current.has(indicator)) cacheRef.current.set(indicator, new Map())
      const countryMap = cacheRef.current.get(indicator)
      countries.forEach((country) => {
        if (!countryMap.has(country)) {
          missing.push({ indicator, country })
        }
      })
    })

    if (missing.length > 0) {
      setLoading(true)
      try {
        await runConcurrent(missing, async (job) => {
          try {
            const { data } = await axios.post('/api/v1/data/indicator-country-metadata/', {
              indicator: job.indicator,
              country: job.country,
            })
            const row = (data.results || [])[0]
            cacheRef.current.get(job.indicator).set(job.country, row ? row.metadata : null)
          } catch (error) {
            console.error('Error fetching indicator-country metadata:', error)
            cacheRef.current.get(job.indicator).set(job.country, null)
          }
        })
      } finally {
        setLoading(false)
      }
    }

    recomputeMerged(indicators)
  }, [recomputeMerged])

  return { metadataByIndicator, loading, ensureMetadata }
}

export default useIndicatorMetadataCache
